import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import FileUploader from "@/components/FileUploader";
import { refreshBranding } from "@/hooks/useBranding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";
import { toast } from "sonner";

export default function SuperSettings() {
  const [f, setF] = useState({
    site_title: "", site_description: "", site_logo_url: "", site_favicon_url: "",
    site_url: "https://schooldial.co.in",
    hero_badge: "", hero_title_line1: "", hero_title_line2: "", hero_subtitle: "", hero_image_url: "",
    sitemap_urls: [],
    robots_txt: "", sitemap_xml: "",
    seo_title_template: "Top 10 Schools in {city_name} {year}",
    seo_meta_template: "Best Schools in {city_name} {year} – Compare top nearby schools, fees, admissions and reviews.",
    city_desc_template: "Looking for admissions in {city_name} for the session {year}? Here is the complete list of top schools with fees, board, reviews and admission status.",
    default_year: "2026-27",
  });

  useEffect(() => { api.get("/settings").then((r) => setF({ ...f, ...r.data })); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/settings", f); await refreshBranding(); toast.success("Saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <DashLayout variant="super" title="Site Settings">
      <form onSubmit={save} className="max-w-3xl space-y-6" data-testid="settings-form">
        <div className="bg-white p-6 rounded-lg border border-slate-200 space-y-4">
          <h3 className="font-bold">Branding</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="mb-1 block">Site Title</Label>
              <Input value={f.site_title} onChange={(e) => setF({ ...f, site_title: e.target.value })} data-testid="settings-title" />
            </div>
            <div>
              <Label className="mb-1 block">Site Description (SEO)</Label>
              <Input value={f.site_description} onChange={(e) => setF({ ...f, site_description: e.target.value })} data-testid="settings-desc" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="mb-1 block">Site Logo <span className="text-xs text-slate-500 font-normal">(shown in header — recommended 200×60 PNG)</span></Label>
              <div className="flex items-center gap-3">
                {f.site_logo_url ? (
                  <div className="relative">
                    <img src={f.site_logo_url} alt="logo" className="h-14 w-auto max-w-[180px] object-contain bg-slate-50 border border-slate-200 rounded px-2 py-1" />
                    <button type="button" onClick={() => setF({ ...f, site_logo_url: "" })} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-0.5" data-testid="settings-logo-clear">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="h-14 w-32 border border-dashed border-slate-300 rounded flex items-center justify-center text-xs text-slate-400">No logo</div>
                )}
                <FileUploader folder="branding" onUploaded={(url) => setF({ ...f, site_logo_url: url })} testId="settings-logo-uploader" />
              </div>
              <Input className="mt-2" placeholder="Or paste image URL" value={f.site_logo_url} onChange={(e) => setF({ ...f, site_logo_url: e.target.value })} data-testid="settings-logo-url" />
            </div>
            <div>
              <Label className="mb-1 block">Site Icon (favicon) <span className="text-xs text-slate-500 font-normal">(browser tab — recommended 32×32 or 64×64 PNG/ICO)</span></Label>
              <div className="flex items-center gap-3">
                {f.site_favicon_url ? (
                  <div className="relative">
                    <img src={f.site_favicon_url} alt="favicon" className="h-14 w-14 object-contain bg-slate-50 border border-slate-200 rounded p-1" />
                    <button type="button" onClick={() => setF({ ...f, site_favicon_url: "" })} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-0.5" data-testid="settings-favicon-clear">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="h-14 w-14 border border-dashed border-slate-300 rounded flex items-center justify-center text-[10px] text-slate-400">No icon</div>
                )}
                <FileUploader folder="branding" onUploaded={(url) => setF({ ...f, site_favicon_url: url })} testId="settings-favicon-uploader" />
              </div>
              <Input className="mt-2" placeholder="Or paste image URL" value={f.site_favicon_url} onChange={(e) => setF({ ...f, site_favicon_url: e.target.value })} data-testid="settings-favicon-url" />
            </div>
          </div>
          <div>
            <Label className="mb-1 block">Site URL <span className="text-xs text-slate-500 font-normal">(used in sitemap.xml — e.g. https://schooldial.co.in)</span></Label>
            <Input value={f.site_url} onChange={(e) => setF({ ...f, site_url: e.target.value })} data-testid="settings-site-url" />
          </div>
        </div>

        {/* ---------- Hero (homepage) editor ---------- */}
        <div className="bg-white p-6 rounded-lg border border-slate-200 space-y-4" data-testid="settings-hero-card">
          <h3 className="font-bold">Homepage Hero</h3>
          <div>
            <Label className="mb-1 block">Hero Background Image <span className="text-xs text-slate-500 font-normal">(max 100 KB)</span></Label>
            <div className="flex items-center gap-3">
              {f.hero_image_url && <img src={f.hero_image_url} alt="hero" className="h-14 w-24 object-cover rounded border" data-testid="settings-hero-preview" />}
              <FileUploader folder="branding/hero" onUploaded={(url) => setF({ ...f, hero_image_url: url })} testId="settings-hero-uploader" maxKb={100} accept="image/*" />
            </div>
            <Input className="mt-2" placeholder="Or paste image URL" value={f.hero_image_url} onChange={(e) => setF({ ...f, hero_image_url: e.target.value })} data-testid="settings-hero-url" />
          </div>
          <div><Label className="mb-1 block">Badge Text</Label><Input value={f.hero_badge} onChange={(e) => setF({ ...f, hero_badge: e.target.value })} data-testid="settings-hero-badge" /></div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label className="mb-1 block">Title — Line 1</Label><Input value={f.hero_title_line1} onChange={(e) => setF({ ...f, hero_title_line1: e.target.value })} data-testid="settings-hero-title1" /></div>
            <div><Label className="mb-1 block">Title — Line 2 (yellow)</Label><Input value={f.hero_title_line2} onChange={(e) => setF({ ...f, hero_title_line2: e.target.value })} data-testid="settings-hero-title2" /></div>
          </div>
          <div><Label className="mb-1 block">Subtitle</Label><Textarea rows={2} value={f.hero_subtitle} onChange={(e) => setF({ ...f, hero_subtitle: e.target.value })} data-testid="settings-hero-subtitle" /></div>
        </div>

        {/* ---------- Sitemap URL Manager ---------- */}
        <div className="bg-white p-6 rounded-lg border border-slate-200 space-y-3" data-testid="settings-sitemap-card">
          <div className="flex items-center justify-between">
            <h3 className="font-bold">Sitemap URLs <span className="text-xs text-slate-500 font-normal">(added to the auto-generated sitemap.xml)</span></h3>
            <Button type="button" size="sm" variant="outline" onClick={() => setF({ ...f, sitemap_urls: [...(f.sitemap_urls || []), { loc: "", priority: 0.5, changefreq: "weekly" }] })} data-testid="add-sitemap-url">+ Add URL</Button>
          </div>
          <p className="text-[11px] text-slate-500">Submit <code>{(f.site_url || "https://schooldial.co.in")}/sitemap.xml</code> to Google Search Console. The XML is served directly at /sitemap.xml and regenerated automatically from schools, city pages, blogs and the URLs below.</p>
          <div className="space-y-2">
            {(f.sitemap_urls || []).map((u, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`sitemap-row-${i}`}>
                <Input className="col-span-6" placeholder="https://schooldial.co.in/page" value={u.loc} onChange={(e) => { const arr = [...f.sitemap_urls]; arr[i] = { ...arr[i], loc: e.target.value }; setF({ ...f, sitemap_urls: arr }); }} data-testid={`sitemap-loc-${i}`} />
                <select className="col-span-3 border rounded px-2 h-9 text-sm" value={u.changefreq || "weekly"} onChange={(e) => { const arr = [...f.sitemap_urls]; arr[i] = { ...arr[i], changefreq: e.target.value }; setF({ ...f, sitemap_urls: arr }); }} data-testid={`sitemap-freq-${i}`}>
                  {["always","hourly","daily","weekly","monthly","yearly","never"].map(x => <option key={x} value={x}>{x}</option>)}
                </select>
                <Input className="col-span-2" type="number" step="0.1" min="0" max="1" value={u.priority ?? 0.5} onChange={(e) => { const arr = [...f.sitemap_urls]; arr[i] = { ...arr[i], priority: Number(e.target.value) }; setF({ ...f, sitemap_urls: arr }); }} data-testid={`sitemap-priority-${i}`} />
                <button type="button" className="col-span-1 text-red-600" onClick={() => { const arr = f.sitemap_urls.filter((_, j) => j !== i); setF({ ...f, sitemap_urls: arr }); }} data-testid={`sitemap-del-${i}`}><X className="w-4 h-4" /></button>
              </div>
            ))}
            {(!f.sitemap_urls || f.sitemap_urls.length === 0) && <div className="text-xs text-slate-400 border border-dashed border-slate-300 rounded p-4 text-center">No custom URLs yet — auto-generated URLs from schools, cities and blogs are always included.</div>}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-slate-200 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold">SEO Templates <span className="text-xs text-slate-500 font-normal">(applied to every city page)</span></h3>
            <div className="text-xs text-slate-500">Placeholders: <code>{"{city_name}"}</code> and <code>{"{year}"}</code></div>
          </div>
          <div>
            <Label className="mb-1 block">Global Year (session)</Label>
            <Input
              className="max-w-xs"
              placeholder="2026-27"
              value={f.default_year}
              onChange={(e) => setF({ ...f, default_year: e.target.value })}
              data-testid="settings-year"
            />
            <p className="text-[11px] text-slate-500 mt-1">Change once — updates every city page across the site.</p>
          </div>
          <div>
            <Label className="mb-1 block">Page Title Template</Label>
            <Input
              value={f.seo_title_template}
              onChange={(e) => setF({ ...f, seo_title_template: e.target.value })}
              placeholder="Top 10 Schools in {city_name} {year}"
              data-testid="settings-title-tpl"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Preview: <span className="text-slate-700 italic">{(f.seo_title_template || "").replace("{city_name}", "Delhi").replace("{year}", f.default_year || "2026-27")}</span>
            </p>
          </div>
          <div>
            <Label className="mb-1 block">Meta Description Template</Label>
            <Textarea
              rows={2}
              value={f.seo_meta_template}
              onChange={(e) => setF({ ...f, seo_meta_template: e.target.value })}
              placeholder="Best Schools in {city_name} {year} – Compare top nearby schools..."
              data-testid="settings-meta-tpl"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Preview: <span className="text-slate-700 italic">{(f.seo_meta_template || "").replace("{city_name}", "Delhi").replace("{year}", f.default_year || "2026-27")}</span>
            </p>
          </div>
          <div>
            <Label className="mb-1 block">Short City Description Template</Label>
            <Textarea
              rows={3}
              value={f.city_desc_template}
              onChange={(e) => setF({ ...f, city_desc_template: e.target.value })}
              placeholder="Looking for admissions in {city_name} for the session {year}?..."
              data-testid="settings-desc-tpl"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Shown on <code>/schools/in-&lt;city&gt;</code> when that city has no custom description.
              Preview: <span className="text-slate-700 italic">{(f.city_desc_template || "").replace("{city_name}", "Delhi").replace("{year}", f.default_year || "2026-27")}</span>
            </p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-slate-200 space-y-4">
          <h3 className="font-bold">robots.txt</h3>
          <Textarea rows={6} className="font-mono text-xs" value={f.robots_txt} onChange={(e) => setF({ ...f, robots_txt: e.target.value })} data-testid="settings-robots" />
          <div className="text-xs text-slate-500">Served at <code>/api/robots.txt</code> (also <code>/robots.txt</code> when configured at ingress)</div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-slate-200 space-y-4">
          <h3 className="font-bold">sitemap.xml</h3>
          <Textarea rows={8} className="font-mono text-xs" value={f.sitemap_xml} onChange={(e) => setF({ ...f, sitemap_xml: e.target.value })} data-testid="settings-sitemap" />
          <div className="text-xs text-slate-500">Served directly at <code>/sitemap.xml</code> — regenerated automatically on save and server start.</div>
        </div>
        <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="settings-submit">Save Settings</Button>
      </form>
      <CityDescriptions />
    </DashLayout>
  );
}

function CityDescriptions() {
  const [items, setItems] = React.useState([]);
  const [f, setF] = React.useState({ city: "", description: "", seo_title: "", meta_description: "" });
  const load = async () => { try { const { data } = await (await import("@/lib/api")).default.get("/city-content"); setItems(data); } catch {} };
  React.useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    const api = (await import("@/lib/api")).default;
    try { await api.post("/city-content", f); (await import("sonner")).toast.success("Saved"); setF({ city: "", description: "", seo_title: "", meta_description: "" }); load(); } catch {}
  };
  const edit = (c) => setF({ city: c.city, description: c.description || "", seo_title: c.seo_title || "", meta_description: c.meta_description || "" });
  const remove = async (city) => {
    if (!window.confirm(`Delete city description for ${city}?`)) return;
    const api = (await import("@/lib/api")).default;
    try { await api.delete(`/city-content/${encodeURIComponent(city)}`); (await import("sonner")).toast.success("Deleted"); load(); } catch {}
  };
  return (
    <div className="max-w-3xl mt-10 space-y-4">
      <h2 className="text-xl font-bold text-slate-900">City Descriptions <span className="text-sm font-normal text-slate-500">(shown on /schools/in-:city pages)</span></h2>
      <form onSubmit={save} className="bg-white p-6 rounded-lg border border-slate-200 space-y-3" data-testid="city-content-form">
        <Input placeholder="City name (e.g. Delhi)" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} required data-testid="cc-city" />
        <Input placeholder="SEO Title" value={f.seo_title} onChange={(e) => setF({ ...f, seo_title: e.target.value })} data-testid="cc-title" />
        <Textarea rows={4} placeholder="City description (shown to users on the city listing page)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} required data-testid="cc-desc" />
        <Textarea rows={2} placeholder="Meta description for search engines" value={f.meta_description} onChange={(e) => setF({ ...f, meta_description: e.target.value })} data-testid="cc-meta" />
        <div className="flex gap-2">
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="cc-submit">Save City Content</Button>
          {f.city && <Button type="button" variant="outline" onClick={() => setF({ city: "", description: "", seo_title: "", meta_description: "" })} data-testid="cc-clear">Clear</Button>}
        </div>
      </form>
      <div className="space-y-3">
        {items.length === 0 && <div className="text-sm text-slate-500 bg-white p-4 rounded-lg border border-slate-200">No city descriptions yet.</div>}
        {items.map((c) => (
          <div key={c.id || c.city} className="relative overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={`cc-row-${c.city}`}>
            <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-blue-600 to-indigo-600" />
            <div className="pl-5 pr-4 py-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-xs uppercase tracking-widest font-bold text-blue-700">City</div>
                <div className="text-lg font-bold text-slate-900 mt-0.5">{c.city}</div>
                <div className="text-sm text-slate-600 mt-2 line-clamp-3 whitespace-pre-wrap">{c.description}</div>
                {c.seo_title && <div className="text-[11px] text-slate-500 mt-2 font-mono">SEO: {c.seo_title}</div>}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => edit(c)} data-testid={`cc-edit-${c.city}`}>Edit</Button>
                <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => remove(c.city)} data-testid={`cc-del-${c.city}`}>Delete</Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}